import { omit } from 'lodash-es';
import { FilterDropdown } from '@ui';
import { AirportStartLocationType } from '@lp/features/RoutePlanner/types';
import { DEFAULT_AIRPORTS_QUERY } from '@lp/features/RoutePlanner/config';
import { useAsyncSearchableList } from '@/hooks/useAsyncSearchableList';
import { useInfiniteAirportsListQueryByCode } from '@/queries/airports';
import { StartLocationObjectType } from '@/api/apis/mission-planner';
import { AirportType } from '@/api/apis/facilities';
import { LegLocationTypeEnum } from '@/api/apis/route';
import { NameByValue } from '@/types';
import { SuggestedSelectTrigger } from './SuggestedSelectTrigger';

type StartLocationAirportDropdownProps = {
  data: AirportStartLocationType | null;
  handleAirportSelect: (airport: AirportStartLocationType | null) => void;
};

const airportsMapper = (item: AirportType): NameByValue & { data: AirportStartLocationType } => ({
  value: item.id,
  name: `${item.iata || item.icao}`,
  data: {
    ...omit(item, ['location', 'fbos']),
    type: StartLocationObjectType.Airport,
    location: item.location,
    locationType: LegLocationTypeEnum.airport,
    fboLocations: item.fbos,
  },
});

export const StartLocationAirportDropdown = ({ data, handleAirportSelect }: StartLocationAirportDropdownProps) => {
  const {
    list: airportsList,
    isLoading,
    hasNextPage,
    loadNextPage,
    setSearchValue: onAirportsSearch,
  } = useAsyncSearchableList({
    query: useInfiniteAirportsListQueryByCode,
    defaultFilters: DEFAULT_AIRPORTS_QUERY,
    mapper: airportsMapper,
  });

  const handleSelect = (keys: string[]) => {
    const selectedAirport = airportsList.find((i) => i.value === keys[0]);

    handleAirportSelect(selectedAirport ? selectedAirport.data : null);
  };

  return (
    <FilterDropdown
      title="Airport"
      placement="top-start"
      items={airportsList}
      disallowEmptySelection
      renderCustomTrigger={(isOpen) => (
        <SuggestedSelectTrigger
          title="Airport:"
          value={data?.id ? `${data.iata || data.icao}` : 'Select'}
          isOpen={isOpen}
        />
      )}
      selectedKeys={data?.id ? [data.id] : []}
      mode="single"
      isLoading={isLoading}
      loadNextPage={loadNextPage}
      hasNextPage={hasNextPage}
      onSearchValue={onAirportsSearch}
      onSelectedKeysChange={handleSelect}
    />
  );
};
